import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { staggerContainer, staggerItem } from '../../../utils/animations';
import { useAuth } from '../../../hooks/useAuth';
import { MaterialAvatar, MATERIAL_AVATAR_SOURCES } from '../../ui';
import { Eyebrow, Field, PageHeader, Panel, Pill } from '../../ui/kit';

const CheckIcon: React.FC<{ className?: string }> = ({
  className = 'w-5 h-5',
}) => (
  <svg
    className={className}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    strokeWidth={2}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="m4.5 12.75 6 6 9-13.5"
    />
  </svg>
);

const ExclamationCircleIcon: React.FC<{ className?: string }> = ({
  className = 'w-5 h-5',
}) => (
  <svg
    className={className}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    strokeWidth={2}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"
    />
  </svg>
);

const ShieldCheckIcon: React.FC<{ className?: string }> = ({
  className = 'w-5 h-5',
}) => (
  <svg
    className={className}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    strokeWidth={2}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M9 12.75 11.25 15 15 9.75m-3-7.036A11.959 11.959 0 0 1 3.598 6 11.99 11.99 0 0 0 3 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285Z"
    />
  </svg>
);

// Common timezones
const TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time (HT)' },
  { value: 'Europe/London', label: 'London (GMT/BST)' },
  { value: 'Europe/Paris', label: 'Central European (CET)' },
  { value: 'Europe/Berlin', label: 'Berlin (CET)' },
  { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
  { value: 'Asia/Shanghai', label: 'Shanghai (CST)' },
  { value: 'Asia/Dubai', label: 'Dubai (GST)' },
  { value: 'Australia/Sydney', label: 'Sydney (AEST)' },
  { value: 'UTC', label: 'UTC' },
];

interface SettingsData {
  profile: {
    id: string;
    name: string;
    avatarUrl: string | null;
    email: string;
    emailVerified: boolean;
    createdAt: string;
  };
  preferences: {
    timezone: string;
    emailNotifications: boolean;
    pushNotifications: boolean;
    weeklyDigest: boolean;
  };
}

const ProfilePage: React.FC = () => {
  const { user, refreshAuth } = useAuth();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [profileForm, setProfileForm] = useState({
    name: '',
    timezone: 'America/New_York',
  });

  const [message, setMessage] = useState<{
    type: 'success' | 'error';
    text: string;
  } | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);

  // Fetch settings
  const { data: settings, isLoading } = useQuery<SettingsData>({
    queryKey: ['settings'],
    queryFn: async () => {
      const response = await axios.get('/api/settings');
      return response.data.data;
    },
  });

  // Update form when settings are loaded
  useEffect(() => {
    if (settings) {
      setProfileForm({
        name: settings.profile.name,
        timezone: settings.preferences.timezone,
      });
    }
  }, [settings]);

  const updateSettingsMutation = useMutation({
    mutationFn: async (data: { name?: string; timezone?: string }) => {
      const response = await axios.patch('/api/settings', data);
      return response.data;
    },
    onSuccess: async () => {
      setMessage({ type: 'success', text: 'Profile updated successfully!' });
      await queryClient.invalidateQueries({ queryKey: ['settings'] });
      await refreshAuth();
      setTimeout(() => setMessage(null), 3000);
    },
    onError: (error: any) => {
      const errorMessage =
        error.response?.data?.error ||
        'Failed to update profile. Please try again.';
      setMessage({ type: 'error', text: errorMessage });
      setTimeout(() => setMessage(null), 3000);
    },
  });

  const finishAvatarUpdate = async (text: string) => {
    await queryClient.invalidateQueries({ queryKey: ['settings'] });
    await refreshAuth();
    setAvatarPreview(null);
    setMessage({ type: 'success', text });
    setTimeout(() => setMessage(null), 3000);
  };

  const avatarError = (error: any) => {
    setAvatarPreview(null);
    setMessage({
      type: 'error',
      text:
        error.response?.data?.error ||
        'Failed to update avatar. Please try again.',
    });
    setTimeout(() => setMessage(null), 4000);
  };

  const selectAvatarMutation = useMutation({
    mutationFn: async (preset: string) => {
      const response = await axios.patch('/api/settings/avatar', { preset });
      return response.data;
    },
    onSuccess: () => finishAvatarUpdate('Avatar updated successfully!'),
    onError: avatarError,
  });

  const uploadAvatarMutation = useMutation({
    mutationFn: async ({ file }: { file: File; preview: string }) => {
      const formData = new FormData();
      formData.append('avatar', file);
      const response = await axios.post('/api/settings/avatar', formData);
      return response.data;
    },
    onMutate: ({ preview }) => setAvatarPreview(preview),
    onSuccess: () => finishAvatarUpdate('Photo uploaded successfully!'),
    onError: avatarError,
    onSettled: (_data, _error, variables) =>
      URL.revokeObjectURL(variables.preview),
  });

  const removeAvatarMutation = useMutation({
    mutationFn: async () => {
      const response = await axios.delete('/api/settings/avatar');
      return response.data;
    },
    onSuccess: () =>
      finishAvatarUpdate('Avatar removed. Your default avatar is back.'),
    onError: avatarError,
  });

  const deleteAccountMutation = useMutation({
    mutationFn: async () => {
      const response = await axios.delete('/api/settings/account');
      return response.data;
    },
    onSuccess: data => {
      setShowDeleteModal(false);
      setMessage({ type: 'success', text: data.message });
      setTimeout(() => setMessage(null), 5000);
    },
    onError: () => {
      setMessage({
        type: 'error',
        text: 'Failed to process deletion request.',
      });
      setTimeout(() => setMessage(null), 3000);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateSettingsMutation.mutate(profileForm);
  };

  const handlePhotoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setMessage({ type: 'error', text: 'Choose a JPEG, PNG, or WebP image.' });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setMessage({ type: 'error', text: 'Avatar must be smaller than 5 MB.' });
      return;
    }

    uploadAvatarMutation.mutate({ file, preview: URL.createObjectURL(file) });
  };

  const avatarBusy =
    selectAvatarMutation.isPending ||
    uploadAvatarMutation.isPending ||
    removeAvatarMutation.isPending;
  const displayedAvatar = avatarPreview || settings?.profile.avatarUrl || null;

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-32 bg-zinc-200 rounded animate-pulse" />
        <Panel className="p-6">
          <div className="space-y-6 max-w-lg">
            <div className="h-10 bg-zinc-200 rounded animate-pulse" />
            <div className="h-10 bg-zinc-200 rounded animate-pulse" />
            <div className="h-10 bg-zinc-200 rounded animate-pulse" />
          </div>
        </Panel>
      </div>
    );
  }

  return (
    <motion.div
      variants={staggerContainer}
      initial="initial"
      animate="animate"
      className="space-y-6"
    >
      <motion.div variants={staggerItem} initial="initial" animate="animate">
        <PageHeader
          eyebrow="Account"
          title="Profile"
          description="Manage your personal information and preferences"
          className="mb-0"
        />
      </motion.div>

      <AnimatePresence>
        {message && (
          <motion.div
            initial={{ opacity: 0, y: -10, height: 0 }}
            animate={{ opacity: 1, y: 0, height: 'auto' }}
            exit={{ opacity: 0, y: -10, height: 0 }}
            className={`flex items-center gap-3 p-4 rounded-xl ${
              message.type === 'success'
                ? 'bg-green-50 border border-green-200'
                : 'bg-red-50 border border-red-200'
            }`}
          >
            {message.type === 'success' ? (
              <CheckIcon className="w-5 h-5 text-green-600" />
            ) : (
              <ExclamationCircleIcon className="w-5 h-5 text-red-600" />
            )}
            <span
              className={
                message.type === 'success' ? 'text-green-800' : 'text-red-800'
              }
            >
              {message.text}
            </span>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.div variants={staggerItem} initial="initial" animate="animate">
        <Panel className="p-6">
          <form onSubmit={handleSubmit} className="max-w-2xl space-y-6">
            <section
              className="space-y-5 border-b border-zinc-200 pb-6"
              aria-labelledby="profile-avatar-title"
            >
              <div>
                <Eyebrow>Profile photo</Eyebrow>
                <p
                  id="profile-avatar-title"
                  className="mt-2 text-sm text-muted"
                >
                  Upload your own photo or choose one of the Foundlio avatars.
                </p>
              </div>

              <div className="flex flex-col gap-5 sm:flex-row sm:items-center">
                <MaterialAvatar
                  seed={user?.id || settings?.profile.id || 'user'}
                  name={profileForm.name || settings?.profile.name || 'User'}
                  src={displayedAvatar}
                  className="h-24 w-24 ring-1 ring-zinc-300"
                  loading="eager"
                />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap gap-2">
                    <Pill
                      type="button"
                      tone="outline"
                      size="sm"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={avatarBusy}
                    >
                      {uploadAvatarMutation.isPending
                        ? 'Uploading…'
                        : 'Upload photo'}
                    </Pill>
                    <Pill
                      type="button"
                      tone="ghost"
                      size="sm"
                      onClick={() => removeAvatarMutation.mutate()}
                      disabled={avatarBusy || !settings?.profile.avatarUrl}
                    >
                      Remove
                    </Pill>
                  </div>
                  <p className="mt-2 text-xs leading-relaxed text-zinc-500">
                    JPEG, PNG, or WebP. Maximum 5 MB. A square image works best.
                  </p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    className="sr-only"
                    onChange={handlePhotoChange}
                    aria-label="Upload profile photo"
                  />
                </div>
              </div>

              <div>
                <p className="mb-3 text-sm font-medium text-primary">
                  Choose an avatar
                </p>
                <div
                  className="grid max-w-xl grid-cols-5 gap-2 sm:grid-cols-10"
                  role="group"
                  aria-label="Foundlio avatars"
                >
                  {MATERIAL_AVATAR_SOURCES.map((source, index) => {
                    const isSelected =
                      settings?.profile.avatarUrl === source && !avatarPreview;
                    return (
                      <button
                        key={source}
                        type="button"
                        aria-label={`Choose Foundlio avatar ${index + 1}`}
                        aria-pressed={isSelected}
                        disabled={avatarBusy}
                        onClick={() => {
                          setAvatarPreview(source);
                          selectAvatarMutation.mutate(source);
                        }}
                        className={`relative h-12 w-12 overflow-hidden rounded-full transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:opacity-60 ${
                          isSelected
                            ? 'ring-2 ring-primary ring-offset-2'
                            : 'ring-1 ring-zinc-300 hover:scale-105 hover:ring-zinc-500'
                        }`}
                      >
                        <img
                          src={source}
                          alt=""
                          className="h-full w-full object-cover"
                        />
                        {isSelected && (
                          <span className="absolute bottom-0 right-0 grid h-4 w-4 place-items-center rounded-full bg-primary text-white ring-1 ring-white">
                            <CheckIcon className="h-3 w-3" />
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            </section>

            <Field label="Full Name" htmlFor="name">
              <input
                type="text"
                id="name"
                value={profileForm.name}
                onChange={e =>
                  setProfileForm({ ...profileForm, name: e.target.value })
                }
                className="input-field"
                placeholder="John Smith"
              />
            </Field>

            <Field
              label="Email Address"
              htmlFor="email"
              hint="Email cannot be changed. Contact support if needed."
            >
              <div className="relative">
                <input
                  type="email"
                  id="email"
                  value={settings?.profile.email || ''}
                  disabled
                  className="input-field cursor-not-allowed bg-zinc-50 pr-10 text-muted sm:pr-28"
                />
                {settings?.profile.emailVerified && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1 text-green-600">
                    <ShieldCheckIcon className="w-4 h-4" />
                    <span className="hidden font-mono text-[11px] uppercase tracking-eyebrow sm:inline">
                      Verified
                    </span>
                  </div>
                )}
              </div>
            </Field>

            <Field
              label="Timezone"
              htmlFor="timezone"
              hint="Used for displaying dates and scheduling notifications"
            >
              <select
                id="timezone"
                value={profileForm.timezone}
                onChange={e =>
                  setProfileForm({ ...profileForm, timezone: e.target.value })
                }
                className="input-field"
              >
                {TIMEZONES.map(tz => (
                  <option key={tz.value} value={tz.value}>
                    {tz.label}
                  </option>
                ))}
              </select>
            </Field>

            <Pill type="submit" disabled={updateSettingsMutation.isPending}>
              {updateSettingsMutation.isPending && (
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
              )}
              Save Changes
            </Pill>
          </form>
        </Panel>
      </motion.div>

      {/* Danger Zone */}
      <motion.div variants={staggerItem} initial="initial" animate="animate">
        <Panel className="overflow-hidden border-red-200">
          <div className="px-6 py-4 border-b border-red-100">
            <Eyebrow className="text-red-700">Danger Zone</Eyebrow>
          </div>
          <div className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-primary">Delete Account</p>
                <p className="text-[15px] text-muted mt-1">
                  Permanently delete your account and all associated data.
                </p>
              </div>
              <Pill onClick={() => setShowDeleteModal(true)} tone="danger">
                Delete Account
              </Pill>
            </div>
          </div>
        </Panel>
      </motion.div>

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {showDeleteModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowDeleteModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl p-6 max-w-md w-full shadow-xl"
              onClick={e => e.stopPropagation()}
            >
              <h3 className="text-lg font-semibold text-primary mb-2">
                Delete Account
              </h3>
              <p className="text-muted mb-6">
                Are you sure you want to delete your account? This action cannot
                be undone. All your data, websites, and subscription will be
                permanently removed.
              </p>
              <div className="flex gap-3 justify-end">
                <Pill onClick={() => setShowDeleteModal(false)} tone="ghost">
                  Cancel
                </Pill>
                <Pill
                  onClick={() => deleteAccountMutation.mutate()}
                  disabled={deleteAccountMutation.isPending}
                  tone="danger"
                >
                  {deleteAccountMutation.isPending
                    ? 'Processing...'
                    : 'Yes, Delete My Account'}
                </Pill>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default ProfilePage;
